import { del, get, put } from '../lib/apiClient';

/**
 * Admin console (FEA-30, docs/FRONTEND-PLAN.md §4.3). Every export here that
 * hits `/admin/*` or `/users/search` is gated server-side on the `system:admin`
 * capability (FND-43 removed the inert grant system; FEA-34 made the role map
 * administrator-configurable) — the
 * frontend additionally restricts the `/admin` route and nav item to admins
 * (see ProtectedRoute's `adminOnly` and Layout's nav filter) as a UX
 * convenience, not the real gate.
 */

/**
 * GET /admin/site-settings — admin JWT required. Returns the full settings
 * record including `updatedAt`/`updatedBy` (the public endpoint below strips
 * `updatedBy`). Resolves to `{ settings }`.
 */
export const getSiteSettings = () => get('/admin/site-settings');

/**
 * PUT /admin/site-settings — admin JWT required. Whole-object replace: the
 * caller must supply every top-level section
 * (announcement/supportEmail/registrationOpen/features) every time; unknown
 * keys 400. Resolves to `{ settings }`.
 */
export const putSiteSettings = settings => put('/admin/site-settings', settings);

/**
 * GET /site-settings/public — NO auth (`AuthorizationType: NONE`). Same
 * shape as the admin read minus `updatedBy`. Callers must pass `{auth:
 * false}` is handled here already; consumed by SiteSettingsProvider so it
 * works pre-login too. Resolves to `{ settings }`.
 *
 * Optional `orgId` query param triggers org-scoped resolution (MTN §4.3):
 * the response merges the org's overrides over the platform defaults.
 */
export const getPublicSiteSettings = orgId =>
  get('/site-settings/public', { auth: false, query: orgId ? { orgId } : undefined });

/**
 * GET /admin/roles — admin JWT required (`system:admin` capability). Resolves
 * to `{ roles, isDefault, updatedAt, updatedBy, availableCapabilities,
 * availableModules, requiredRoles }`, where `roles` maps a role name to
 * `{ label, capabilities[], modules[] }`.
 *
 * `isDefault: true` means no record has been saved yet and the platform is
 * running on the compiled-in defaults. The vocabularies come from the server
 * so this UI never renders a capability list that has drifted from the one the
 * gates actually consult.
 */
export const getRoles = () => get('/admin/roles');

/**
 * PUT /admin/roles — admin JWT required. Whole-object replace: send the
 * complete map every time, since a dropped role is treated as a deletion.
 * 400s on an unknown capability or module, on removing the last role holding
 * `system:admin`, and on removing a role the platform assigns by name. Every
 * save is audit-logged with before/after. Resolves like the GET.
 */
export const putRoles = roles => put('/admin/roles', { roles });

/**
 * GET /admin/security-settings - admin JWT required: `system:admin` or the
 * platform-admin role (docs/Decisions.md D49). Resolves
 * to `{ settings, availableScanners }`: the effective file-scanning policy
 * (`settings.fileScanning` plus `updatedAt`/`updatedBy`) and the scanner
 * catalogue, each `{ id, displayName, configured }`. `configured: false` means
 * the provider is selectable but not deployed in this environment. The read is
 * audit-logged.
 */
export const getSecuritySettings = () => get('/admin/security-settings');

/**
 * PUT /admin/security-settings - same gate as the GET. Patch semantics over
 * `fileScanning` (`enabled`, `scanners[]`, `aggregation`, `failMode`,
 * `timeoutMs`); 400s with a list of messages on an unknown scanner id or an
 * out-of-range value. Audit-logged with before/after. Resolves like the GET.
 */
export const putSecuritySettings = patch => put('/admin/security-settings', patch);

/**
 * GET /admin/org-site-settings — org-admin JWT + X-Org-Id header required
 * (MTN §4.3). Returns the org's own settings overrides including
 * `updatedAt`/`updatedBy`. Resolves to `{ settings }`.
 */
export const getOrgSiteSettings = () => get('/admin/org-site-settings');

/**
 * PUT /admin/org-site-settings — org-admin JWT + X-Org-Id header required.
 * Whole-object replace of the org's settings overrides. Same shape as
 * platform site settings. Resolves to `{ settings }`.
 */
export const putOrgSiteSettings = settings => put('/admin/org-site-settings', settings);

/**
 * GET /users/search?role=&status=&emailPrefix=&limit=&lastEvaluatedKey= —
 * admin JWT required (hard-coded admin gate in the handler, not the inert
 * permission system). NOT a free-text `q` param. Resolves to
 * `{ users: PublicUser[], count, lastEvaluatedKey }`; PublicUser carries
 * userId/email/username/firstName/lastName/role/groups (no permissions).
 */
export const searchUsers = query => get('/users/search', { query });

/**
 * GET /admin/policy-settings - `system:admin` or platform-admin
 * (docs/Decisions.md D53). The platform default layer every organization
 * inherits. Resolves to `{ platform: { layer, updatedAt, updatedBy }, defaults,
 * resolved: { values, sources }, fields }`.
 */
export const getPolicySettings = () => get('/admin/policy-settings');

/** PUT /admin/policy-settings - replace the platform default layer. Resolves like the GET. */
export const putPolicySettings = policy => put('/admin/policy-settings', { policy });

/**
 * GET /admin/policy-settings/orgs/{orgId} - one org's overrides. Resolves to
 * `{ orgId, override, platform, resolved: { values, sources }, fields }`.
 */
export const getOrgPolicySettings = orgId =>
  get(`/admin/policy-settings/orgs/${encodeURIComponent(orgId)}`);

/** PUT /admin/policy-settings/orgs/{orgId} - replace one org's overrides. */
export const putOrgPolicySettings = (orgId, policy) =>
  put(`/admin/policy-settings/orgs/${encodeURIComponent(orgId)}`, { policy });

/** DELETE /admin/policy-settings/orgs/{orgId} - the org follows the platform default again. */
export const clearOrgPolicySettings = orgId =>
  del(`/admin/policy-settings/orgs/${encodeURIComponent(orgId)}`);
